
import { supabase } from "@/integrations/supabase/client";
import { ModalCampaignUserLog } from "@/types/modalCampaigns";

/**
 * Create a log entry when a modal is displayed to a user
 */
export const createModalLog = async (campaignId: string, userId: string): Promise<ModalCampaignUserLog> => {
  const { data, error } = await supabase
    .from('modal_campaign_user_logs')
    .insert({
      campaign_id: campaignId,
      user_id: userId,
      displayed_at: new Date().toISOString()
    })
    .select()
    .single();

  if (error) {
    console.error('Error creating modal log:', error);
    throw error;
  }

  return data;
};

/**
 * Update a log entry when a modal is dismissed
 */
export const updateModalLog = async (logId: string, dismissedPermanently: boolean = false): Promise<void> => {
  const { error } = await supabase
    .from('modal_campaign_user_logs')
    .update({
      dismissed_at: new Date().toISOString(),
      dismissed_permanently: dismissedPermanently
    })
    .eq('id', logId);

  if (error) {
    console.error('Error updating modal log:', error);
    throw error;
  }
};

/**
 * Check if a modal should be shown based on frequency and logs
 */
export const shouldShowModal = async (
  campaignId: string, 
  userId: string, 
  frequency: string
): Promise<boolean> => {
  // Check if user has permanently dismissed this modal
  const { data: permanentDismiss } = await supabase
    .from('modal_campaign_user_logs')
    .select('*')
    .eq('campaign_id', campaignId)
    .eq('user_id', userId)
    .eq('dismissed_permanently', true)
    .limit(1);

  if (permanentDismiss && permanentDismiss.length > 0) {
    return false;
  }

  // Special case for every_login frequency - always show
  if (frequency === 'every_login') {
    return true;
  }

  // Get latest log for this campaign and user
  const { data: latestLog } = await supabase
    .from('modal_campaign_user_logs')
    .select('*')
    .eq('campaign_id', campaignId)
    .eq('user_id', userId)
    .order('displayed_at', { ascending: false })
    .limit(1);

  if (!latestLog || latestLog.length === 0) {
    return true; // No logs found, show the modal
  }

  const lastDisplayed = new Date(latestLog[0].displayed_at);
  const now = new Date();

  // Check based on frequency
  switch (frequency) {
    case 'once':
      return !latestLog[0].dismissed_at; // Show only if not dismissed yet
    case 'every_minute':
      return now.getTime() - lastDisplayed.getTime() >= 60 * 1000; // 1 minute
    case 'hourly':
      return now.getTime() - lastDisplayed.getTime() >= 60 * 60 * 1000; // 1 hour
    case 'daily':
      return now.getTime() - lastDisplayed.getTime() >= 24 * 60 * 60 * 1000; // 24 hours
    case 'weekly':
      return now.getTime() - lastDisplayed.getTime() >= 7 * 24 * 60 * 60 * 1000; // 7 days
    case 'monthly':
      return now.getTime() - lastDisplayed.getTime() >= 30 * 24 * 60 * 60 * 1000; // ~30 days
    default:
      return true;
  }
};
